import { useSearchParams } from 'react-router-dom';

import { Container, EmptyState, PageHeader, Section } from '@/components/common';
import { routes } from '@/config/routes';
import { mapPublicCategoryToCategoryCardModel } from '@/features/catalog/api/catalogMappers';
import { CategoryGrid } from '@/features/catalog/components';
import { useCatalog } from '@/features/catalog/context';
import { usePublicCategories } from '@/features/catalog/hooks';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q')?.trim() ?? '';
  const { media } = useCatalog();
  const categoriesQuery = usePublicCategories({ limit: 12 });
  const categories = (categoriesQuery.data?.items ?? []).map((category) =>
    mapPublicCategoryToCategoryCardModel(category, media),
  );
  useDocumentMetadata(query ? `Search for ${query}` : 'Search');

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const nextQuery = String(form.get('q') ?? '').trim();
    setSearchParams(nextQuery ? { q: nextQuery } : {});
  }

  return (
    <>
      <PageHeader
        eyebrow="Catalog discovery"
        title={query ? `Search is being upgraded` : 'What are you hoping to create?'}
        description="Full-catalog text search is not yet supported by the public catalog API. Browse a category or use the supported filters instead."
        breadcrumbs={[{ label: 'Home', to: routes.home }, { label: 'Search' }]}
      />
      <Section spacing="large">
        <Container size="wide">
          <form className="search-page-form" role="search" onSubmit={submit}>
            <label htmlFor="search-page-input">Search products</label>
            <div>
              <input
                id="search-page-input"
                name="q"
                className="p-inputtext p-component"
                defaultValue={query}
                placeholder="Search will be available in a future catalog milestone"
              />
              <button className="p-button p-component" type="submit">
                Check search availability
              </button>
            </div>
          </form>
          <EmptyState
            icon="pi pi-info-circle"
            title={
              query
                ? `Complete results for “${query}” are not available yet`
                : 'Browse the live catalog while search is being upgraded'
            }
            description="We do not search a single paginated result and present it as the full catalog. Category, product type, badge, featured, and sort filters are available now."
            actionLabel="Browse filtered products"
            actionTo={routes.products}
          />
          {categories.length > 0 && (
            <div className="search-category-browse">
              <h2>Browse by category</h2>
              <CategoryGrid categories={categories} />
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}
